/**
 * Arriving (02 §5 Spawning): each monster slides straight down from Y $15 through the platforms,
 * 2 px per I-tick, to its spawn row - frozen play for the first 44 frames ($17BE..$17FF), in play
 * after (L_1CA0) - then waits out its delay (level delay x 2 I-ticks) before its AI starts
 * (D_1CBD $1D11). Also the escape from a bubble: the fall of state $0A (D_2808).
 */

import { probe } from './map';
import { fallStep, idleAnim } from './motion';
import { DROP_COOLDOWN, DROP_STEP, MAX_X, MIN_X } from './tuning';
import type { Monster, World } from '../types';

/**
 * One drop-in tick. Reaching the spawn row is noticed on the tick after the last step. Landing in
 * play sets the attack cooldown to 10 (L_1CAD); landing during the frozen start leaves the drop
 * target there instead, as the frozen loop never resets it (a C64 quirk, $17EE; matters to M2).
 */
export function dropInTick(m: Monster, frozen: boolean): void {
  const a = m.ai;
  if (m.y === a.targetY) {
    a.cooldown = frozen ? a.targetY : DROP_COOLDOWN;
    if (!frozen) a.attack = -1;
    m.state = a.delay > 0 ? 'waiting' : 'active';
  } else {
    m.y = (m.y + DROP_STEP) & 0xff;
  }
  idleAnim(m);
}

/**
 * $1D11: while the delay runs it counts down and the monster only animates; the tick it reaches 0
 * is still a waiting tick. Returns true when the monster waited this tick.
 */
export function waitTick(m: Monster): boolean {
  const a = m.ai;
  if (a.delay <= 0) return false;
  a.delay--;
  idleAnim(m);
  if (a.delay === 0 && m.state === 'waiting') m.state = 'active';
  return true;
}

/**
 * D_2808: an angry monster just out of its bubble (the BUBBLE stream sets `state = 'escaping'`) falls
 * 2 px an update until it lands, then carries on as before. Set-up as at the release ($13E4): X
 * made even, Y odd (so it meets the 8-px lines), facing right, no delay. Landing puts it back in its
 * kind's state with the jump and fall state cleared and X clamped to $24..$F4 (an Invader's X also
 * aligned to 4 px). Its anger gives it extra updates like any other, so it falls 1.5x as fast.
 */
export function escapeTick(world: World, m: Monster): void {
  const a = m.ai;
  if (!a.escaping) {
    a.escaping = true;
    m.x &= 0xfe;
    m.y |= 1;
    a.frame = 0;
    a.delay = 0;
  }
  const p = probe(m);
  idleAnim(m);
  if (!fallStep(world, m, p)) return;
  if (m.kind === 'invader') m.x = ((m.x - 0x14) & 0xfc) + 0x14;
  m.x = Math.min(MAX_X, Math.max(MIN_X, m.x));
  a.jump = -1;
  a.descent = -1;
  a.falling = false;
  a.attack = -1;
  a.escaping = false;
  m.state = 'active';
}
